import React from 'react';
import { Recycle, TrendingUp, Globe, Star, Building2, User } from 'lucide-react';
import { NamedLink } from '../../../components';

/**
 * AboutSection: pixel-for-pixel port of the Replit about.tsx page.
 * Used as a custom PageBuilder section for the About page.
 *
 * Registered in SECTION_TYPE_MAP as 'constructionAbout'.
 * Props: sectionData (PageBuilder), blockData (unused: all content hardcoded from Replit design)
 */
const AboutSection = () => (
  <div className="flex flex-col w-full">

    {/* ── The Billion Dollar Problem ── */}
    <section className="py-24 bg-secondary text-secondary-foreground">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 uppercase tracking-tight">
              The Billion Dollar Problem
            </h2>
            <p className="text-lg text-secondary-foreground/80 mb-6">
              The construction industry is one of the largest waste generators in the world. Every year, perfectly usable lumber, concrete, metals, and fixtures are thrown away simply because it's easier than finding a buyer.
            </p>
            <p className="text-lg text-secondary-foreground/80">
              We built ConstructionYardSale.com to change the math. By making it incredibly simple to list and find local materials, we're making sustainability profitable.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="bg-card/10 border border-white/10 p-6 rounded-xl text-center">
              <div className="text-4xl font-bold text-primary mb-2">600M+</div>
              <div className="text-sm font-medium uppercase tracking-wider text-secondary-foreground/70">Tons of C&D Debris</div>
              <div className="text-xs text-secondary-foreground/50 mt-2">Generated annually in the US alone</div>
            </div>
            <div className="bg-card/10 border border-white/10 p-6 rounded-xl text-center">
              <div className="text-4xl font-bold text-accent mb-2">30%</div>
              <div className="text-sm font-medium uppercase tracking-wider text-secondary-foreground/70">Of Total Waste</div>
              <div className="text-xs text-secondary-foreground/50 mt-2">Construction accounts for nearly a third of all landfill waste</div>
            </div>
            <div className="bg-card/10 border border-white/10 p-6 rounded-xl text-center sm:col-span-2">
              <div className="text-4xl font-bold text-green-400 mb-2">$5 Billion</div>
              <div className="text-sm font-medium uppercase tracking-wider text-secondary-foreground/70">Lost Value</div>
              <div className="text-xs text-secondary-foreground/50 mt-2">Estimated value of reusable building materials discarded every year</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── Our Mission ── */}
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 text-center max-w-4xl">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 uppercase tracking-tight">
          Our Mission
        </h1>
        <p className="text-xl md:text-2xl text-muted-foreground font-medium leading-relaxed mb-12">
          To build a zero-waste future for the construction industry by connecting those with excess materials to those who need them.
        </p>
        <div className="grid sm:grid-cols-3 gap-8">
          {[
            { Icon: Recycle, bg: 'bg-primary/10', color: 'text-primary', title: 'Eliminate Waste', desc: 'Keep perfectly good building materials out of overflowing landfills.' },
            { Icon: TrendingUp, bg: 'bg-secondary/10', color: 'text-secondary', title: 'Recover Costs', desc: 'Turn sunk project costs back into working capital for your business.' },
            { Icon: Globe, bg: 'bg-accent/10', color: 'text-accent', title: 'Build Local', desc: 'Strengthen local economies by sourcing materials directly from your community.' },
          ].map(({ Icon, bg, color, title, desc }) => (
            <div key={title} className="flex flex-col items-center">
              <div className={`h-16 w-16 ${bg} rounded-full flex items-center justify-center mb-4`}>
                <Icon className={`h-8 w-8 ${color}`} />
              </div>
              <h3 className="font-bold text-lg uppercase mb-2">{title}</h3>
              <p className="text-sm text-muted-foreground">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Who Is This For? ── */}
    <section className="py-20 bg-background border-y border-border">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-6 uppercase tracking-tight">Who Is This For?</h2>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {[
            {
              Icon: Building2,
              accentCls: 'bg-primary',
              iconCls: 'text-primary',
              title: 'Businesses & Contractors',
              desc: 'Liquidate excess inventory from completed jobs. Turn wasted materials into recovered capital and free up valuable warehouse space.',
              bullets: ['Recover sunk costs', 'Improve sustainability metrics'],
              linkTo: 'SignupPage',
              linkParams: { userType: 'business' },
              btnLabel: 'Register as Business',
            },
            {
              Icon: User,
              accentCls: 'bg-secondary',
              iconCls: 'text-secondary',
              title: 'Individuals & DIYers',
              desc: 'Find high-quality materials at a fraction of retail cost for your home projects, or sell leftover supplies from your own renovations.',
              bullets: ['Save money on projects', 'Keep materials out of landfills'],
              linkTo: 'SignupPage',
              linkParams: {},
              btnLabel: 'Register as Individual',
            },
          ].map(({ Icon, accentCls, iconCls, title, desc, bullets, linkTo, linkParams, btnLabel }) => (
            <div key={title} className="group relative overflow-hidden rounded-xl border border-border bg-card p-8 shadow-sm hover:shadow-md transition-all text-left">
              <div className={`absolute top-0 left-0 w-2 h-full ${accentCls}`} />
              <div className="flex items-center justify-center w-full mb-6">
                <Icon className={`h-12 w-12 ${iconCls}`} />
              </div>
              <h3 className="text-2xl font-bold uppercase mb-3 text-center">{title}</h3>
              <p className="text-muted-foreground mb-6 text-center">{desc}</p>
              <ul className="space-y-2 mb-8">
                {bullets.map(b => (
                  <li key={b} className="flex items-center gap-2 text-sm font-medium">
                    <TrendingUp className="h-4 w-4 text-green-500 flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <NamedLink name={linkTo} params={linkParams} className="block w-full rounded-md border border-input bg-background px-4 py-2 text-sm font-semibold uppercase text-center hover:bg-accent">
                {btnLabel}
              </NamedLink>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Environmental Impact Stats ── */}
    <section className="py-24 bg-background">
      <div className="container mx-auto px-4 max-w-5xl">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center uppercase tracking-tight">
          Our Environmental Impact So Far
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
          {[
            { value: '1.2M', color: 'text-green-600', label: 'Pounds of Waste Diverted' },
            { value: '$4.5M', color: 'text-blue-600', label: 'Saved by Buyers' },
            { value: '8,500+', color: 'text-amber-600', label: 'Trees Saved' },
            { value: '12,000', color: 'text-purple-600', label: 'Successful Trades' },
          ].map(({ value, color, label }) => (
            <div key={label} className="p-6 bg-muted/30 rounded-2xl border border-border/50">
              <div className={`text-5xl font-bold ${color} mb-3`}>{value}</div>
              <div className="font-bold uppercase text-sm text-muted-foreground">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Why Buy & Sell With Us? ── */}
    <section className="py-24 bg-muted/30 border-y border-border/50">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 uppercase tracking-tight">
            Why Buy & Sell With Us?
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Whether you're a large contractor clearing out a yard, or a DIYer looking for affordable supplies, the impact of buying and selling used materials goes far beyond the transaction.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-12">
          {/* Economic */}
          <div className="bg-card p-8 rounded-2xl border border-border shadow-sm">
            <h3 className="text-2xl font-bold uppercase mb-6 text-primary flex items-center gap-3">
              <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                <TrendingUp className="h-5 w-5" />
              </div>
              Economic Impact
            </h3>
            <ul className="space-y-6">
              {[
                ['Turn Sunk Costs into Revenue', "Don't pay disposal fees to throw away perfectly good materials. Sell them and recover your project costs."],
                ['Empower Small Businesses', 'Small contractors and tradespeople gain access to premium materials at steep discounts, allowing them to compete on bids and increase their profit margins.'],
                ['Keep Money Local', 'Instead of buying from massive national retailers, your money stays within your local community, supporting local tradespeople and contractors.'],
              ].map(([h, p]) => (
                <li key={h}>
                  <h4 className="font-bold text-lg mb-2">{h}</h4>
                  <p className="text-muted-foreground">{p}</p>
                </li>
              ))}
            </ul>
          </div>
          {/* Environmental */}
          <div className="bg-card p-8 rounded-2xl border border-border shadow-sm">
            <h3 className="text-2xl font-bold uppercase mb-6 text-green-600 flex items-center gap-3">
              <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                <Recycle className="h-5 w-5" />
              </div>
              Environmental Impact
            </h3>
            <ul className="space-y-6">
              {[
                ['Reduce Landfill Burden', 'Construction and demolition (C&D) debris accounts for massive volumes of municipal solid waste. Every item sold is one less item in a landfill.'],
                ['Lower Carbon Emissions', 'Manufacturing new materials requires massive amounts of energy. Buying locally used materials significantly reduces the carbon footprint of your project.'],
                ['Preserve Natural Resources', 'Reusing lumber saves trees. Reusing metals reduces mining impact. Reusing masonry conserves water and aggregates.'],
              ].map(([h, p]) => (
                <li key={h}>
                  <h4 className="font-bold text-lg mb-2">{h}</h4>
                  <p className="text-muted-foreground">{p}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* ── Community Success Stories ── */}
    <section className="py-24 bg-background">
      <div className="container mx-auto px-4 max-w-6xl">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center uppercase tracking-tight">
          Community Success Stories
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { amount: '$800', badge: 'bg-green-50 text-green-700 ring-green-600/20', quote: '"We had three pallets of commercial pavers left over from a big job. Instead of paying to haul them away, we sold them in 48 hours and recovered $800."', name: 'Sarah M.', role: 'Commercial Contractor' },
            { amount: '$1,200', badge: 'bg-blue-50 text-blue-700 ring-blue-600/20', quote: '"I\'m renovating an old Victorian and the cost of new lumber is insane. I found perfectly aged, old-growth 2x4s here for a fraction of the cost."', name: 'David L.', role: 'DIY Homeowner' },
            { amount: '$2,450', badge: 'bg-green-50 text-green-700 ring-green-600/20', quote: '"As a small fencing company, being able to buy excess materials locally allows us to underbid our larger competitors while keeping our margins high."', name: 'Marcus T.', role: 'Small Business Owner' },
          ].map(({ amount, badge, quote, name, role }) => (
            <div key={name} className="bg-muted/20 p-8 rounded-xl border border-border flex flex-col">
              <div className="flex text-yellow-500 mb-4">
                {[0,1,2,3,4].map(i => <Star key={i} className="h-5 w-5 fill-current" />)}
              </div>
              <div className="mb-4">
                <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset uppercase tracking-wide ${badge}`}>
                  Listing Amount: {amount}
                </span>
              </div>
              <p className="italic text-lg mb-6 leading-relaxed flex-grow">{quote}</p>
              <div className="font-bold uppercase">{name}</div>
              <div className="text-sm text-muted-foreground">{role}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export default AboutSection;
